<template>
    <div class="ds-ex-tab">
        <VacancyQuickForm v-if="canCreate" @created="onCreated" />

        <div class="ds-ex-toolbar">
            <div class="ds-glass-segment ds-ex-segment" role="group" aria-label="Статус вакансий">
                <button
                    v-for="item in SEGMENTS"
                    :key="item.id"
                    type="button"
                    class="ds-glass-segment-option"
                    :class="{ 'is-active': f.segment === item.id }"
                    :aria-pressed="f.segment === item.id"
                    @click="f.segment = item.id"
                >
                    {{ item.label }}
                    <span v-if="segmentCount(item.id) !== null" class="ds-ex-count" :class="{ 'is-dark': item.id === 'needs_decision' && segmentCount(item.id)! > 0 }">{{ segmentCount(item.id) }}</span>
                </button>
            </div>
            <span class="ds-ex-divider" aria-hidden="true"></span>
            <ExchangePeriodFilter v-model="f.period" />
            <EmployeeFilter v-model="f.positionIds" label="Должность" search-placeholder="Найти должность" :options="positionOptions" />
            <ExchangeOptionFilter v-model="urgencyModel" label="Срочность" :options="URGENCY_OPTIONS" />
            <ExchangeOptionFilter v-if="showExchangeType" v-model="exchangeTypeModel" label="Тип биржи" :options="EXCHANGE_TYPE_OPTIONS" />
            <div class="ds-ex-spacer"></div>
            <ExchangeSearch v-model="f.search" />
        </div>

        <ExchangeFilterChips v-if="chips.length" :chips="chips" :found="summary?.matched_count ?? null" :total="summary?.total_in_period ?? null" @remove="removeChip" @reset="resetFilters" />

        <div v-if="outside" class="ds-ex-outside">
            <span>Ещё {{ countLabel(outside.count, VACANCY_FORMS) }} {{ outside.count === 1 ? 'требует' : 'требуют' }} решения вне выбранного периода</span>
            <button type="button" class="ds-ex-link-btn" @click="showOutside">Показать</button>
        </div>

        <div v-if="backgroundError" class="ds-ex-bg-error" role="status">
            <span>Не удалось обновить данные</span>
            <button type="button" class="ds-ex-link-btn" @click="load('background')">Повторить</button>
        </div>

        <div class="ds-ex-table" role="table" aria-label="Мои вакансии" :aria-busy="status === 'loading'">
            <div class="ds-ex-grid ds-ex-head" :class="monthMode ? 'ds-ex-grid-month' : 'ds-ex-grid-vacancies'" role="row">
                <span v-if="!monthMode"></span>
                <span>{{ monthMode ? 'Время' : 'Когда' }}</span><span>Должность</span><span>Биржа</span><span>Статус</span><span>Отклики</span><span class="ds-ex-right">Действие</span>
            </div>
            <template v-if="status === 'loading'">
                <div v-for="index in 6" :key="index" class="ds-ex-grid ds-ex-row ds-ex-grid-vacancies" aria-hidden="true">
                    <span></span>
                    <span class="ds-ex-skeleton w-[90px]"></span>
                    <span class="ds-ex-skeleton w-[160px]"></span>
                    <span class="ds-ex-skeleton w-[70px]"></span>
                    <span class="ds-ex-skeleton w-[90px]"></span>
                    <span class="ds-ex-skeleton w-[140px]"></span>
                    <span class="ds-ex-skeleton w-[80px] ml-auto"></span>
                </div>
            </template>
            <div v-else-if="status === 'error'" class="ds-ex-state" role="alert">
                <span>Не удалось загрузить вакансии</span>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="load('reset')">Повторить</button>
            </div>
            <template v-else>
                <div v-if="showNothingToDecide" class="ds-ex-quiet">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                    {{ NOTHING_TO_DECIDE }}
                </div>
                <div v-if="!items.length && (hasConditions || !showNothingToDecide)" class="ds-ex-state">
                    <template v-if="hasConditions">
                        <span>Ничего не нашли</span>
                        <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="resetFilters">Сбросить всё</button>
                    </template>
                    <span v-else>{{ f.segment === 'needs_decision' ? NOTHING_TO_DECIDE : 'За выбранный период вакансий нет' }}</span>
                </div>
                <template v-for="entry in entries" :key="entry.key">
                    <div v-if="entry.type === 'group'" class="ds-ex-group">{{ entry.label }}</div>
                    <div v-else-if="entry.type === 'day'" class="ds-ex-day">
                        <span class="ds-ex-strong">{{ entry.label }}</span>
                        <span v-if="entry.today" class="ds-ex-chip is-brand is-small">сегодня</span>
                    </div>
                    <VacancyRow
                        v-else
                        :vacancy="entry.vacancy"
                        :expanded="page.expanded.includes(entry.vacancy.id)"
                        :busy="actions.isBusy(entry.vacancy.id)"
                        :is-new="page.createdIds.includes(entry.vacancy.id)"
                        :can-decide="canDecide"
                        :can-manage="canCreate"
                        :compact="monthMode"
                        :now="now"
                        @toggle="toggleExpanded(entry.vacancy.id)"
                        @open="tab => emit('open', entry.vacancy, tab)"
                        @confirm="user => actions.confirmResponse(entry.vacancy, user)"
                        @reject="user => actions.rejectResponse(entry.vacancy, user)"
                        @remove="emit('remove', entry.vacancy)"
                    />
                </template>
            </template>
        </div>

        <Pager v-if="status !== 'error'" :total="count" v-model:page="f.page" v-model:page-size="f.pageSize" />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import EmployeeFilter from '@/components/employees/EmployeeFilter.vue'
import Pager from '@/components/employees/Pager.vue'
import ExchangeFilterChips, { type ExchangeChip } from './ExchangeFilterChips.vue'
import ExchangeOptionFilter from './ExchangeOptionFilter.vue'
import ExchangePeriodFilter from './ExchangePeriodFilter.vue'
import ExchangeSearch from './ExchangeSearch.vue'
import VacancyQuickForm from './VacancyQuickForm.vue'
import VacancyRow from './VacancyRow.vue'
import { useExchangeActions } from './exchangeActions'
import { useExchangeDirectory } from './exchangeDirectory'
import { useExchangePage, toggleIn } from './exchangePage'
import { useExchangeList } from './useExchangeList'
import {
    countLabel,
    dayHeaderLabel,
    dayKeyOf,
    EXCHANGE_TYPE_LABELS,
    needsDecision,
    todayKey,
    VACANCY_FORMS,
    wallNow,
} from './exchangeFormat'
import type { ExchangeType, ListQuery, Vacancy, VacancySegment } from './exchangeTypes'
import dayjs from 'dayjs'

const props = defineProps<{ active: boolean }>()
const emit = defineEmits<{ open: [vacancy: Vacancy, tab: 'vacancy' | 'history'], remove: [vacancy: Vacancy] }>()

const SEGMENTS: Array<{ id: VacancySegment, label: string }> = [
    { id: 'active', label: 'Активные' },
    { id: 'needs_decision', label: 'Нужно решение' },
    { id: 'waiting_responses', label: 'Ждут откликов' },
    { id: 'closed', label: 'Закрытые' },
]
const URGENCY_OPTIONS = [{ id: 'true', name: 'Горящие' }, { id: 'false', name: 'Не горящие' }]
const EXCHANGE_TYPE_OPTIONS = (Object.keys(EXCHANGE_TYPE_LABELS) as ExchangeType[]).map(id => ({ id, name: EXCHANGE_TYPE_LABELS[id] }))
const NOTHING_TO_DECIDE = 'Решать пока нечего. Как только кто-то откликнется, вакансия поднимется наверх.'

const authStore = useAuthStore()
const page = useExchangePage()
const f = page.vacancies
const directory = useExchangeDirectory()

const canCreate = computed(() => Boolean(authStore.permissions?.can_create_vacancy_exchange))
const canDecide = computed(() => Boolean(authStore.permissions?.can_confirm_user_for_vacancy))
const showExchangeType = computed(() => Boolean(authStore.settings?.USE_EXCHANGE_PORTAL))
const positionOptions = computed(() => directory.positions.value)

const query = computed<ListQuery>(() => ({
    page: f.page,
    pageSize: f.pageSize,
    period: f.period,
    search: f.search,
    positionIds: f.positionIds,
    isUrgent: f.isUrgent,
    exchangeType: f.exchangeType,
    segment: f.segment,
}))

// Любой фильтр, кроме страницы, возвращает на первую страницу.
// Объявлено до списка: сброс страницы применяется в том же цикле, без второго запроса.
watch(() => [f.segment, f.period.join(), f.positionIds.join(), f.isUrgent, f.exchangeType, f.search, f.pageSize], () => { f.page = 1 })

const { items, count, status, backgroundError, summary, load } = useExchangeList('vacancies', query, {
    active: toRef(props, 'active'),
    onPageOverflow: last => { f.page = last },
})
const actions = useExchangeActions(() => load('action'))

// Исчезнувшая запись не удерживается раскрытой искусственно.
watch(items, list => {
    const ids = new Set(list.map(item => item.id))
    page.expanded = page.expanded.filter(id => ids.has(id))
})

// Настенное «сейчас» магазина — для «Горящая» и запрета действий с начала смены.
const now = ref(wallNow())
const clock = setInterval(() => { now.value = wallNow() }, 60_000)
onBeforeUnmount(() => clearInterval(clock))

const urgencyModel = computed<string[]>({
    get: () => f.isUrgent === null ? [] : [String(f.isUrgent)],
    set: value => { f.isUrgent = value.length ? value[0] === 'true' : null },
})
const exchangeTypeModel = computed<string[]>({
    get: () => f.exchangeType ? [f.exchangeType] : [],
    set: value => { f.exchangeType = (value[0] as ExchangeType | undefined) ?? null },
})

const segmentCount = (id: VacancySegment) => summary.value?.segments?.[id] ?? null
const hasConditions = computed(() => Boolean(f.positionIds.length || f.isUrgent !== null || f.exchangeType || f.search))

const chips = computed<ExchangeChip[]>(() => {
    const list: ExchangeChip[] = f.positionIds.map(id => ({ key: `position:${id}`, label: directory.positions.value.find(item => item.id === id)?.name ?? 'Должность' }))
    if (f.isUrgent !== null) list.push({ key: 'urgent', label: f.isUrgent ? 'Горящие' : 'Не горящие' })
    if (f.exchangeType) list.push({ key: 'exchange', label: EXCHANGE_TYPE_LABELS[f.exchangeType] })
    return list
})

function removeChip(key: string) {
    if (key.startsWith('position:')) f.positionIds = f.positionIds.filter(id => `position:${id}` !== key)
    else if (key === 'urgent') f.isUrgent = null
    else if (key === 'exchange') f.exchangeType = null
}

function resetFilters() {
    f.positionIds = []
    f.isUrgent = null
    f.exchangeType = null
    f.search = ''
}

const outside = computed(() => {
    const value = summary.value?.needs_decision_outside_period
    return value && value.count > 0 && value.date_from && value.date_to ? value : null
})

// «Показать»: сегмент «Нужно решение», охватывающий период, дополнительные фильтры сброшены.
function showOutside() {
    const value = outside.value
    if (!value) return
    const from = [f.period[0], value.date_from!].sort()[0]
    const to = [f.period[1], value.date_to!].sort()[1]
    resetFilters()
    f.segment = 'needs_decision'
    f.period = [from, to]
}

// Месяц и длинные периоды — заголовки дней; они вторичны группе и не переставляют строки страницы.
const monthMode = computed(() => dayjs(f.period[1]).diff(dayjs(f.period[0]), 'day') >= 7)

const showNothingToDecide = computed(() => f.segment === 'active' && f.page === 1 && summary.value?.segments?.needs_decision === 0 && !hasConditions.value)

type Entry =
    | { type: 'group', key: string, label: string }
    | { type: 'day', key: string, label: string, today: boolean }
    | { type: 'row', key: string, vacancy: Vacancy }

const entries = computed<Entry[]>(() => {
    const result: Entry[] = []
    const today = todayKey()
    let lastGroup = ''
    let lastDay = ''
    for (const vacancy of items.value) {
        const group = f.segment === 'active' ? (needsDecision(vacancy) ? 'decision' : 'waiting') : ''
        if (group && group !== lastGroup) {
            const total = summary.value?.segments?.[group === 'decision' ? 'needs_decision' : 'waiting_responses']
            const label = group === 'decision' ? 'НУЖНО РЕШЕНИЕ' : 'ЖДУТ ОТКЛИКОВ'
            result.push({ type: 'group', key: `group:${group}`, label: total === undefined ? label : `${label} · ${total}` })
            lastGroup = group
            lastDay = ''
        }
        const day = dayKeyOf(vacancy.start)
        if (monthMode.value && day !== lastDay) {
            result.push({ type: 'day', key: `day:${group}:${day}`, label: dayHeaderLabel(day), today: day === today })
            lastDay = day
        }
        result.push({ type: 'row', key: vacancy.id, vacancy })
    }
    return result
})

function toggleExpanded(id: string) {
    page.expanded = toggleIn(page.expanded, id, !page.expanded.includes(id))
}

function onCreated(ids: string[]) {
    page.createdIds = [...new Set([...page.createdIds, ...ids])]
    void load('action')
}

defineExpose({ refresh: () => load('action') })
</script>
